import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Footer } from './components';
import BannerImage from '../assets/banner.png';
import GlobalHeader from '../components/GlobalHeader';
import { LPSectionHero } from './components/LPSectionHero';
import LPSectionInfluencers from './components/LPSectionInfluencers';
import { LPSectionBrands } from './components/LPSectionBrands';
import LPSectionProducts from './components/LPSectionProducts';
import LPSectionBlogs from './components/LPSectionBlogs';
import PaddedContainer from '../components/PaddedContainer ';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import useAuthToken from '../hooks/useAuthToken';
import { useEffect, useRef, useState } from 'react';
import useNavigation, { StoreError } from '../hooks/useNavigation';
import userClient from '../utils/apiClients/userClient';
import { handleSignOut } from '../handlers/signOut';
import LoadingIcon from '../icons/LoadingIcon';
import { signIn } from 'aws-amplify/auth';

// ─── Comparison Block (§6) ────────────────────────────────────────────────────

const comparisonRows = [
  {
    label: "What you're buying",
    saagas: 'The complete creator engine, run by a dedicated Growth Operator',
    marketplace: 'Creative supply — you run the engine',
    inhouse: 'A team you have to hire and manage',
  },
  {
    label: 'Warmed-up account network',
    saagas: 'Persona-matched, ready to distribute on Day 1',
    marketplace: 'None — you post to your own accounts',
    inhouse: 'Build from scratch (6–9 months)',
  },
  {
    label: 'Per-creator MMP attribution',
    saagas: 'Built in (AppsFlyer, Adjust, Branch)',
    marketplace: 'None',
    inhouse: 'Requires custom build',
  },
  {
    label: 'Volume capability',
    saagas: 'Hundreds of videos / month',
    marketplace: 'One-off creative supply',
    inhouse: 'Tops out at ~20-30 / month',
  },
  {
    label: 'Performance reporting',
    saagas: 'Custom, per-creator, per-program',
    marketplace: 'None',
    inhouse: 'Custom build required',
  },
  {
    label: 'Time to first content',
    saagas: '15 days',
    marketplace: 'Days — but no engine behind it',
    inhouse: '6–9 months to stand up',
  },
];

const ComparisonBlock = () => {
  const theme = useTheme();
  const isMdOrBelow = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 8, md: 10 },
        px: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Header */}
      <Typography fontFamily="Inter" sx={{ fontSize: '12px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#0CAECF', mb: 2, textAlign: 'center' }}>
        The honest comparison
      </Typography>
      <Typography fontFamily="Inter" component="h2" sx={{ fontWeight: 700, fontSize: { xs: '22px', md: '34px' }, color: '#FFFFFF', textAlign: 'center', mb: 2, maxWidth: 700 }}>
        Why not just use a UGC marketplace or build in-house?
      </Typography>
      <Typography fontFamily="Inter" sx={{ fontWeight: 300, fontSize: { xs: '14px', md: '16px' }, color: '#C0C0C0', textAlign: 'center', mb: 6, maxWidth: 620 }}>
        Most growth teams are comparing three paths: a creative-supply marketplace, an in-house build, or a managed engine. Here&apos;s the honest read.
      </Typography>

      {/* Desktop table */}
      {!isMdOrBelow && (
        <Box sx={{ width: '100%', maxWidth: 1100, overflowX: 'auto' }}>
          {/* Column headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1.4fr 1.4fr', gap: '1px', mb: '1px' }}>
            <Box sx={{ p: 2 }} />
            <Box sx={{ p: 2, background: 'rgba(12,174,207,0.12)', border: '1px solid rgba(12,174,207,0.4)', borderRadius: '8px 8px 0 0', textAlign: 'center' }}>
              <Typography fontFamily="Inter" sx={{ fontWeight: 700, fontSize: '15px', color: '#0CAECF' }}>Saagas</Typography>
            </Box>
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography fontFamily="Inter" sx={{ fontWeight: 600, fontSize: '13px', color: '#808080' }}>UGC marketplaces</Typography>
              <Typography fontFamily="Inter" sx={{ fontSize: '11px', color: '#606060' }}>Billo, Insense, Trend</Typography>
            </Box>
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography fontFamily="Inter" sx={{ fontWeight: 600, fontSize: '13px', color: '#808080' }}>In-house build</Typography>
            </Box>
          </Box>

          {/* Rows */}
          {comparisonRows.map(({ label, saagas, marketplace, inhouse }, i) => (
            <Box
              key={label}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.6fr 1.4fr 1.4fr',
                gap: '1px',
                background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
              }}
            >
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '13px', fontWeight: 600, color: '#A0A0A0' }}>{label}</Typography>
              </Box>
              <Box sx={{ p: 2, background: 'rgba(12,174,207,0.06)', borderLeft: '1px solid rgba(12,174,207,0.3)', borderRight: '1px solid rgba(12,174,207,0.3)', display: 'flex', alignItems: 'center' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#FFFFFF', lineHeight: '150%' }}>{saagas}</Typography>
              </Box>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#808080', lineHeight: '150%' }}>{marketplace}</Typography>
              </Box>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#808080', lineHeight: '150%' }}>{inhouse}</Typography>
              </Box>
            </Box>
          ))}

          {/* Saagas column bottom border */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1.4fr 1.4fr' }}>
            <Box />
            <Box sx={{ borderLeft: '1px solid rgba(12,174,207,0.4)', borderRight: '1px solid rgba(12,174,207,0.4)', borderBottom: '1px solid rgba(12,174,207,0.4)', borderRadius: '0 0 8px 8px', height: '8px', background: 'rgba(12,174,207,0.06)' }} />
          </Box>
        </Box>
      )}

      {/* Mobile card stack — Saagas-emphasized layout */}
      {isMdOrBelow && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 500 }}>
          {comparisonRows.map(({ label, saagas, marketplace, inhouse }) => (
            <Box
              key={label}
              sx={{
                border: '1px solid rgba(12,174,207,0.25)',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'rgba(12,174,207,0.03)',
              }}
            >
              {/* Category */}
              <Box sx={{ px: 2, py: 1.25, background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '11px', fontWeight: 700, color: '#A0A0A0', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                  {label}
                </Typography>
              </Box>

              {/* Saagas — the winner, highlighted */}
              <Box sx={{ px: 2, py: 2, background: 'rgba(12,174,207,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <Box component="span" sx={{ color: '#0CAECF', fontSize: '14px', lineHeight: 1 }}>★</Box>
                  <Typography fontFamily="Inter" sx={{ fontSize: '11px', fontWeight: 700, color: '#0CAECF', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Saagas
                  </Typography>
                </Box>
                <Typography fontFamily="Inter" sx={{ fontSize: '15px', color: '#FFFFFF', fontWeight: 500, lineHeight: '140%' }}>
                  {saagas}
                </Typography>
              </Box>

              {/* "Compared to" divider */}
              <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '10px', fontWeight: 600, color: '#606060', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Compared to
                </Typography>
              </Box>

              {/* Alternatives, muted */}
              <Box sx={{ px: 2, py: 1.25, mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '11px', fontWeight: 600, color: '#909090', mb: 0.25 }}>UGC marketplaces</Typography>
                <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#808080', lineHeight: '140%' }}>{marketplace}</Typography>
              </Box>
              <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <Typography fontFamily="Inter" sx={{ fontSize: '11px', fontWeight: 600, color: '#909090', mb: 0.25 }}>In-house build</Typography>
                <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#808080', lineHeight: '140%' }}>{inhouse}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Closing qualifier */}
      <Typography fontFamily="Inter" sx={{ fontSize: '14px', color: '#808080', textAlign: 'center', mt: 5, maxWidth: 520, fontStyle: 'italic' }}>
        Marketplaces may be a fit if you already have a creator manager, a warmed-account network, hook and script variant testing, and attribution wired up. Most growth-stage app teams don&apos;t.
      </Typography>
    </Box>
  );
};

// ─── Pricing ──────────────────────────────────────────────────────────────────

const pricingTiers = [
  {
    name: 'Experiment',
    price: '$7,950',
    per: '/mo',
    terms: '$23,850 total · 90 days · No annual contract',
    tag: null as string | null,
    features: [
      '9 creators sourced and managed end-to-end',
      '54 TikTok ready videos per month',
      '162 videos tested across 90 days',
      'Warmed-account distribution',
      'Per-creator MMP attribution',
      'Weekly performance reporting',
      'Hand-off-ready for your existing paid agency',
    ],
    idealFor: 'Brands validating UGC before scaling. First content delivered within 15 days of Kick-off call.',
  },
  {
    name: 'Launch',
    price: '$14,900',
    per: '/mo',
    terms: '90-day pilot, then month-to-month',
    tag: '★ Most Popular' as string | null,
    features: [
      'Everything in Experiment',
      '20 creators sourced and managed end-to-end',
      '110 TikTok and 110 Meta ready videos per month',
      '660 total videos tested across 90 days',
      'Variant leaderboard + weekly performance update',
      'Bi-weekly strategy reviews',
    ],
    idealFor: 'Brands with a validated creative direction ready to scale variant velocity.',
  },
  {
    name: 'Scale',
    price: '$29,000',
    per: '/mo',
    terms: 'For high-growth aggressive paid programs',
    tag: null as string | null,
    features: [
      'Everything in Launch',
      '45 creators sourced and managed end-to-end',
      '225 TikTok and 225 Meta ready videos per month',
      '1,350 videos tested across 90 days',
      'Full performance reporting + creator-level attribution',
      'Organic posting on owned channels (optional)',
      'Dedicated account operator',
      'Custom reporting dashboard',
      'Weekly strategy sync',
    ],
    idealFor: 'High-growth brands running aggressive paid social programs who need maximum variant velocity.',
  },
];

const PricingSection = () => {
  return (
    <Box
      id="pricing"
      sx={{
        overflow: 'hidden',
        width: '100%',
        backgroundImage: `url(${BannerImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 4 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.75)',
          zIndex: 0,
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', mb: 4 }}>
        <Typography fontFamily="Inter" sx={{ fontSize: '12px', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: '#0CAECF', mb: 1.5 }}>
          Pricing — public, all-inclusive
        </Typography>
        <Typography
          fontFamily="Inter"
          sx={{ fontWeight: 700, fontSize: { xs: '24px', md: '36px' }, color: '#FFFFFF', mb: 1 }}
        >
          Most agencies don&apos;t publish pricing. We do.
        </Typography>
        <Typography fontFamily="Inter" sx={{ fontWeight: 300, fontSize: { xs: '14px', md: '16px' }, color: '#C0C0C0', maxWidth: 600, mx: 'auto' }}>
          All tiers include strategy, creator sourcing, briefing, production, warmed-account distribution, attribution, and reporting. No hidden creator fees.
        </Typography>
      </Box>

      {/* Tier cards */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: '1.5rem',
          maxWidth: 1100,
          mx: 'auto',
          mb: 4,
        }}
      >
        {pricingTiers.map(({ name, price, per, terms, tag, features, idealFor }) => (
          <Box
            key={name}
            sx={{
              border: tag ? '1px solid rgba(12,174,207,0.6)' : '1px solid rgba(255,255,255,0.15)',
              background: tag ? 'rgba(12,174,207,0.1)' : 'rgba(0,0,0,0.6)',
              borderRadius: '12px',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {tag && (
              <Box sx={{ backgroundColor: '#0CAECF', color: '#000', fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', px: 1.5, py: 0.5, borderRadius: '20px', alignSelf: 'flex-start' }}>
                {tag}
              </Box>
            )}
            <Typography fontFamily="Inter" sx={{ fontSize: '12px', fontWeight: 600, color: '#A0A0A0', textTransform: 'uppercase', letterSpacing: '1.5px' }}>{name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography fontFamily="Inter" sx={{ fontSize: '36px', fontWeight: 800, color: '#FFFFFF', lineHeight: 1 }}>{price}</Typography>
              <Typography fontFamily="Inter" sx={{ fontSize: '16px', color: '#A0A0A0' }}>{per}</Typography>
            </Box>
            <Typography fontFamily="Inter" sx={{ fontSize: '12px', color: '#606060' }}>{terms}</Typography>
            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 1.5 }} />
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              {features.map(f => (
                <Box component="li" key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: '14px', color: '#C0C0C0', fontFamily: 'Inter' }}>
                  <Box component="span" sx={{ color: '#0CAECF', mt: '1px', flexShrink: 0 }}>✓</Box> {f}
                </Box>
              ))}
            </Box>
            <Typography fontFamily="Inter" sx={{ fontSize: '12px', color: '#808080', mt: 0.5, fontStyle: 'italic' }}>
              <strong style={{ color: '#A0A0A0', fontStyle: 'normal' }}>Ideal for:</strong> {idealFor}
            </Typography>
            <Box
              component="a"
              href="https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                textAlign: 'center',
                background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
                color: '#fff',
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '14px',
                py: 1.25,
                borderRadius: '170px',
                textDecoration: 'none',
                mt: 1,
                '&:hover': { opacity: 0.9 }
              }}
            >
              Book a Kickoff Call →
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#808080', mb: 0.5 }}>
          Running a B2B brand instead?{' '}
          <Link to="/b2b" style={{ color: '#0CAECF', textDecoration: 'underline' }}>See the B2B program →</Link>
        </Typography>
        <Typography fontFamily="Inter" sx={{ fontSize: '13px', color: '#808080' }}>
          90-day pilot, then month-to-month. No annual contract. No kill fees.
        </Typography>
      </Box>
    </Box>
  );
};

// ─── FAQ ──────────────────────────────────────────────────────────────────────

const faqs = [
  {
    q: "What kind of apps do you work with?",
    a: "Growth-stage and scaled mobile apps already spending on TikTok and Meta — typically Series A+ or bootstrapped equivalent, with a paid acquisition agency or in-house team already running. We're not built for pre-launch apps still validating the product.\n\nThat said, we do selectively work with early-stage apps when we're part of a clear GTM launch plan and there's a strong path to paid acquisition from day one.",
  },
  {
    q: "How fast can we get content live?",
    a: "First content delivered within 15 days of Kick-off call. That includes creator briefing, production, QA, and TikTok account warming. We don't cut corners on the warm-up — it directly impacts reach.",
  },
  {
    q: "Do we have to switch our paid agency or change workflow?",
    a: "No. You can keep your existing agency, in-house team, and paid media setup.\n\nWe deliver Meta- and TikTok-ready cuts posted organically from our own pre-warmed accounts, then identify the strongest-performing content during the program. From there, you can work with us to turn winning posts into paid ads without changing your workflow, migrating accounts, or disrupting current vendors.",
  },
  {
    q: "What's included in the price — are creator fees extra?",
    a: "All-inclusive. Creator sourcing, contracting, briefing, production, warmed-account distribution, QA, and reporting are all covered in the monthly fee. No hidden creator fees. No surprise pass-throughs.",
  },
  {
    q: "How does attribution work?",
    a: "Every creator and every TikTok account gets its own deep link, tied to your MMP — AppsFlyer, Adjust, Branch. You see installs and CPI broken down by creator and account in your dashboard.",
  },
  {
    q: "How do you compare to Influencer marketplaces (Billo, Insense, Trend)?",
    a: "Different category. Marketplaces are self-serve creative supply — you brief, you cast, you post, you track. We're a managed organic playbook with warmed accounts and attribution built in.",
  },
  {
    q: "How do you compare to building it in-house?",
    a: "Most app teams that try take 6–9 months and need dedicated UGC + growth headcount before they ship a single video. The warmed-account network specifically is where in-house builds stall. We're live in 15 days of Kick-off call, on a 90-day minimum pilot, then month-to-month.",
  },
  {
    q: "What's the minimum commitment?",
    a: "90-day pilot engagement, then month-to-month. No annual contract. No kill fees. Cancel after 90 days if it's not working — no questions asked.",
  },
  {
    q: "What do you need from us to get started?",
    a: "Four things: confirmation of your target personas / ICPs; brand voice guidelines; a single approver for briefs and final content; MMP integration (we'll help scope if needed).",
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <Box
      component="section"
      id="faq"
      sx={{
        pt: { md: '6rem' },
        pb: { md: '4rem' },
        px: { xs: '1rem', md: '2rem' },
        maxWidth: '800px',
        mx: 'auto',
        width: '100%',
        scrollMarginTop: '80px',
      }}
    >
      <Typography
        fontFamily="Inter"
        component="h2"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '24px', md: '34px' },
          color: '#FFFFFF',
          textAlign: 'center',
          mb: 4
        }}
      >
        Common questions.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {faqs.map(({ q, a }, i) => (
          <Box key={q} sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden' }}>
            <Box
              component="button"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                px: 2.5,
                py: 2,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: '#FFFFFF',
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '14px', md: '15px' },
                '&:hover': { background: 'rgba(255,255,255,0.04)' }
              }}
            >
              <span>{q}</span>
              <Box
                component="span"
                sx={{
                  ml: 2,
                  flexShrink: 0,
                  transition: 'transform 0.2s',
                  transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                  color: '#808080',
                  fontSize: '18px'
                }}
              >
                ▾
              </Box>
            </Box>
            {openIndex === i && (
              <Box sx={{ px: 2.5, pb: 2.5, background: 'rgba(255,255,255,0.03)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {a.split(/\n\n+/).map((para, pi) => (
                  <Typography key={pi} fontFamily="Inter" sx={{ fontSize: '14px', color: '#C0C0C0', lineHeight: '160%' }}>
                    {para}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── B2B Callout ─────────────────────────────────────────────────────────────

const B2BCallout = () => (
  <Box sx={{ textAlign: 'center', mt: 10, mb: 4, px: { xs: 2, md: 0 } }}>
    <Box sx={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px', p: { xs: 4, md: 6 }, maxWidth: 600, mx: 'auto', background: 'rgba(255,255,255,0.02)' }}>
      <Typography fontFamily="Inter" component="h2" sx={{ fontWeight: 700, fontSize: { xs: '22px', md: '28px' }, color: '#FFFFFF', mb: 2 }}>
        Run a B2B brand instead?
      </Typography>
      <Typography fontFamily="Inter" sx={{ fontWeight: 300, fontSize: { xs: '14px', md: '16px' }, color: '#C0C0C0', lineHeight: '160%', mb: 3 }}>
        We run a parallel program for B2B SaaS and service brands whose founder content is fatiguing on paid Meta. Same team, same creator network, different playbook tuned for B2B funnels.
      </Typography>
      <Link to="/b2b" style={{ display: 'inline-block', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '170px', padding: '10px 24px', color: '#FFFFFF', textDecoration: 'none', fontSize: '14px', fontWeight: 600, fontFamily: 'Inter' }}>
        See the B2B program →
      </Link>
    </Box>
  </Box>
);

// ─── Final CTA ────────────────────────────────────────────────────────────────

const FinalCTA = () => (
  <Box sx={{ textAlign: 'center', mt: 10, pb: { xs: '3rem', md: '5rem' }, px: { xs: '1rem', md: '0' } }}>
    <Typography fontFamily="Inter" component="h2" sx={{ fontWeight: 700, fontSize: { xs: '28px', md: '40px' }, color: '#FFFFFF', mb: 2 }}>
      Ready to ship hundreds of creator videos a month?
    </Typography>
    <Typography fontFamily="Inter" sx={{ fontWeight: 300, fontSize: { xs: '15px', md: '18px' }, color: '#C0C0C0', mb: 4, maxWidth: 560, mx: 'auto' }}>
      Book a 30-minute discovery call. We&apos;ll tell you honestly whether Saagas is the right fit — and which tier makes sense for where you are.
    </Typography>
    <Box
      component="a"
      href="https://outlook.office.com/bookwithme/user/eb7d87d7154e461ab27627de6f7d2e9e@saagas.ai?anonymous&ismsaljsauthenabled&ep=plink"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-block',
        background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
        color: '#fff',
        fontFamily: 'Inter',
        fontWeight: 700,
        fontSize: '16px',
        px: 4,
        py: 2,
        borderRadius: '170px',
        textDecoration: 'none',
        boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
        mb: 2,
        '&:hover': { opacity: 0.9 }
      }}
    >
      Book a Discovery Call →
    </Box>
  </Box>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const LandingPage = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState<boolean>(false);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const { isAuthenticated, isLoading: tokenLoading } = useAuthToken();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      if (loading) return;
      try {
        setLoading(true);
        if (!isAuthenticated) return;
        const { data }: { data: { user: object } } = await userClient.post('/login', {});
        if (!data.user) {
          setIsWhitelisted(true);
          return;
        }
      } catch (error) {
        StoreError('An error is generated while calling api /login ', error);
        await handleSignOut();
        throw error;
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [isAuthenticated, loading, navigation]);

  // Scroll to a #section from the URL (e.g. /#pricing). useNavigation() re-navigates signed-out
  // visitors to '/', which strips the hash before the page content renders, so remember it and
  // retry after each render until the target section exists.
  const pendingHash = useRef(location.hash);
  useEffect(() => {
    if (location.hash) pendingHash.current = location.hash;
    const hash = pendingHash.current;
    if (!hash) return;
    const targetElement = document.querySelector(hash);
    if (targetElement) {
      pendingHash.current = '';
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  });

  useEffect(() => {
    const logs = JSON.parse(localStorage.getItem('errorLogs') || '[]');
    if (logs.length > 0) {
      logs.forEach(log => {
        console.error(log);
      });
      localStorage.removeItem('errorLogs');
    }
  }, []);

  if (loading || tokenLoading || window.location.href.includes('app')) {
    return (
      <div className="grid place-items-center h-screen w-full">
        <div>
          <LoadingIcon />
          <p className="font-inter text-semibold mt-4">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (isWhitelisted) {
    return (
      <div className="grid place-items-center h-screen w-full">
        <div className="flex flex-col items-center">
          <LoadingIcon width={180} />
          <p className="font-inter text-semibold mt-4">Thank you for your interest!</p>
          <p className="font-inter text-semibold mt-4">You are on the waitlist. Please wait, while we get you onboard!</p>
        </div>
      </div>
    );
  }

  if (navigation) {
    return navigation;
  }

  return (
    <div className="overflow-hidden bg-green cursor-default">
      <>
        <GlobalHeader />

        {/* Hero */}
        <PaddedContainer className="">
          <LPSectionHero />
        </PaddedContainer>

        {/* The Saagas System */}
        <PaddedContainer bgColor="#0C0211">
          <LPSectionInfluencers />
        </PaddedContainer>

        {/* How It Works */}
        <Box id="how-it-works">
          <PaddedContainer bgColor="#151515">
            <LPSectionBrands />
          </PaddedContainer>
        </Box>

        {/* Why Saagas */}
        <PaddedContainer bgColor="#0000">
          <LPSectionProducts />
        </PaddedContainer>

        {/* Benchmarks (desktop only, matching original blog section slot) */}
        <PaddedContainer bgColor="linear-gradient(195.36deg, rgba(16, 166, 202, 0.2) -110.46%, rgba(86, 20, 110, 0.2) 100%)">
          <Box
            className="max-w-[1256px] m-auto relative px-6 md:pb-20"
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <LPSectionBlogs />
          </Box>
        </PaddedContainer>

        {/* Comparison block §6 */}
        <PaddedContainer bgColor="#0C0211">
          <ComparisonBlock />
        </PaddedContainer>

        {/* Pricing (reuses the banner image slot) */}
        <PaddedContainer>
          <PricingSection />
        </PaddedContainer>


        {/* FAQ + B2B Callout + Final CTA (replaces original FAQ link + desktop CTA) */}
        <PaddedContainer>
          <FAQSection />
          <B2BCallout />
          <FinalCTA />
        </PaddedContainer>

        <LoginTestComponent />
        <Footer />
      </>
    </div>
  );
};

export default LandingPage;

const LoginTestComponent = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await signIn({ username, password });
      await userClient.post('/login', {});
      navigate('/artist');
    } catch (error) {
      console.error(error, 'error');
    }
  };

  return (
    <form data-cy="login-form" onSubmit={handleSubmit} className="opacity-0 absolute -zIndex-1">
      <input data-cy="username-input" className="text-black" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
      <input
        data-cy="password-input"
        className="text-black"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Password"
      />
      <button data-cy="submit-button">Login</button>
    </form>
  );
};
